import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import "./Modal.css"

  export const Modal = ({ children, open, className, onBackdropClick, fromServicesStudios = false }) => {
  const dialog = useRef();
  const backdropRef = useRef();

  useEffect(() => {
    const modal = dialog.current;
    const isIOSSafari =
      typeof window !== "undefined" &&
      /iP(ad|hone|od)/.test(window.navigator.userAgent) &&
      /Safari/.test(window.navigator.userAgent) &&
      !/CriOS|FxiOS|EdgiOS/.test(window.navigator.userAgent);
    const shouldUseDivModal = isIOSSafari || className === "availabilityTimeModal";

    // Capture original body styles to restore on cleanup
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalWidth = document.body.style.width;
    const scrollY = window.scrollY || 0;

    if (open) {
      // Use native dialog behavior where possible (non-iOS Safari).
      if (!shouldUseDivModal && modal && modal.showModal) {
        try {
          modal.showModal();
        } catch (e) {
          // Fallback for browsers where showModal fails
          console.warn('showModal failed, using fallback');
        }
      }
      
      // Stronger iOS Safari scroll lock: prevents page bounce behind modal.
      document.body.style.overflow = 'hidden';
      if (isIOSSafari) {
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
      }
      
      // iOS Safari viewport fix
      const setVh = () => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      };
      setVh();
      window.addEventListener('resize', setVh);
      
      return () => {
        window.removeEventListener('resize', setVh);
        // Always restore body scroll on unmount
        document.body.style.overflow = originalOverflow || '';
        document.body.style.position = originalPosition || '';
        document.body.style.top = originalTop || '';
        document.body.style.width = originalWidth || '';
        if (isIOSSafari) {
          window.scrollTo(0, scrollY);
        }
        if (!shouldUseDivModal && modal && modal.close) {
          try { modal.close(); } catch {}
        }
      };
    } else {
      if (!shouldUseDivModal && modal && modal.close) {
        modal.close();
      }
      // Restore body scroll
      document.body.style.overflow = originalOverflow || '';
      document.body.style.position = originalPosition || '';
      document.body.style.top = originalTop || '';
      document.body.style.width = originalWidth || '';
      if (isIOSSafari) {
        window.scrollTo(0, scrollY);
      }
    }
  }, [open, className]);

  const handleBackdropClick = (e) => {
    // Improved touch handling for iOS Safari
    if (e.target === dialog.current || e.target === backdropRef.current) {
      e.preventDefault();
      e.stopPropagation();
      onBackdropClick?.(e);
    }
  };

  const handleTouchStart = (e) => {
    // Prevent touch events from bubbling on iOS Safari
    if (e.target === dialog.current || e.target === backdropRef.current) {
      // e.preventDefault();
      e.stopPropagation();
    }
  };

  if (!open) return null;

  const isIOSSafari =
    typeof window !== "undefined" &&
    /iP(ad|hone|od)/.test(window.navigator.userAgent) &&
    /Safari/.test(window.navigator.userAgent) &&
    !/CriOS|FxiOS|EdgiOS/.test(window.navigator.userAgent);
  const shouldUseDivModal = isIOSSafari || className === "availabilityTimeModal";
  const portalRoot = document.getElementById('modal') || document.body;

  return createPortal(
    <>
      {/* Custom backdrop for better iOS Safari support */}
      <div 
        ref={backdropRef}
        className={`modal-backdrop ${className ? `${className}-backdrop` : ''}`}
        onClick={handleBackdropClick}
        onTouchStart={handleTouchStart}
      />
      {shouldUseDivModal ? (
        <div
          ref={dialog}
          className={`modal ${className || ''}`}
          onClick={handleBackdropClick}
          onTouchStart={handleTouchStart}
          role="dialog"
          aria-modal="true"
        >
          {children}
        </div>
      ) : (
        <dialog 
          ref={dialog} 
          className={`modal ${className || ''}`} 
          onClick={handleBackdropClick}
          onTouchStart={handleTouchStart}
          aria-modal="true"
          open={open}
        >
          {children}
        </dialog>
      )}
    </>,
    portalRoot
  );
}

export default Modal;